/**
 * Coach Check-In Responses — view athlete check-in history + configure schedule.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, RefreshControl, Switch,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import {
  loadCheckInResponses,
  loadCheckInConfigs,
  saveCheckInConfig,
  DEFAULT_QUESTIONS,
  type CheckInResponse,
  type CheckInConfig,
} from '@/lib/coachCheckIns';
import { ArrowLeft, ClipboardCheck, Settings2 } from 'lucide-react-native';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CoachCheckInResponses() {
  const { theme } = useTheme();
  const c = theme.colors;
  const router = useRouter();
  const { profile } = useAuth();
  const { clientId, clientName } = useLocalSearchParams<{
    clientId: string;
    clientName: string;
  }>();

  const [responses, setResponses] = useState<CheckInResponse[]>([]);
  const [config, setConfig] = useState<CheckInConfig>({
    clientId: clientId ?? '',
    enabled: false,
    days: [],
    questions: DEFAULT_QUESTIONS,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [activeSection, setActiveSection] = useState<'responses' | 'configure'>('responses');

  const loadData = useCallback(async () => {
    if (!profile?.id || !clientId) return;
    try {
      const [resp, configs] = await Promise.all([
        loadCheckInResponses(profile.id, clientId),
        loadCheckInConfigs(profile.id),
      ]);
      setResponses(resp);
      const existing = configs.find((c) => c.clientId === clientId);
      if (existing) setConfig(existing);
    } catch (err) {
      console.warn('[CoachCheckInResponses] load error', err);
    }
  }, [profile?.id, clientId]);

  useEffect(() => {
    setLoading(true);
    loadData().finally(() => setLoading(false));
  }, [loadData]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const toggleDay = async (dayIdx: number) => {
    const newDays = config.days.includes(dayIdx)
      ? config.days.filter((d) => d !== dayIdx)
      : [...config.days, dayIdx].sort((a, b) => a - b);
    const updated = { ...config, days: newDays };
    setConfig(updated);
    if (profile?.id) {
      setSavingConfig(true);
      try {
        await saveCheckInConfig(profile.id, updated);
      } finally {
        setSavingConfig(false);
      }
    }
  };

  const toggleEnabled = async (val: boolean) => {
    const updated = { ...config, enabled: val };
    setConfig(updated);
    if (profile?.id) {
      setSavingConfig(true);
      try {
        await saveCheckInConfig(profile.id, updated);
      } finally {
        setSavingConfig(false);
      }
    }
  };

  const formatAnswer = (val: string | number | boolean): string => {
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    return String(val);
  };

  const getQuestionLabel = (id: string): string => {
    const q = DEFAULT_QUESTIONS.find((q) => q.id === id);
    return q?.label ?? id;
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <ActivityIndicator color={c.accent} style={{ marginTop: 60 }} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => router.back()}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          style={styles.backBtn}
        >
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={[styles.headerTitle, { color: c.text }]}>Check-Ins</Text>
          <Text style={[styles.headerSub, { color: c.textSecondary }]} numberOfLines={1}>
            {clientName ?? clientId}
          </Text>
        </View>
      </View>

      {/* Section Tabs */}
      <View style={[styles.tabRow, { borderColor: c.border }]}>
        {(['responses', 'configure'] as const).map((tab) => (
          <TouchableOpacity
            key={tab}
            style={[styles.tab, activeSection === tab && { borderBottomColor: c.accent, borderBottomWidth: 2 }]}
            onPress={() => setActiveSection(tab)}
          >
            <Text style={[styles.tabText, { color: activeSection === tab ? c.accent : c.textSecondary }]}>
              {tab === 'responses' ? 'Responses' : 'Configure'}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {activeSection === 'configure' ? (
        <ScrollView contentContainerStyle={styles.scroll}>
          <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <View style={styles.configRow}>
              <Text style={[styles.configLabel, { color: c.text }]}>Enable Check-Ins</Text>
              <Switch
                value={config.enabled}
                onValueChange={toggleEnabled}
                trackColor={{ false: c.border, true: c.accent }}
                thumbColor="#fff"
                disabled={savingConfig}
              />
            </View>
            <Text style={[styles.configHint, { color: c.textTertiary }]}>
              When enabled, athlete will be reminded on the selected days.
            </Text>
          </View>

          <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={[styles.sectionTitle, { color: c.text }]}>SCHEDULE DAYS</Text>
            <View style={styles.daysRow}>
              {DAY_LABELS.map((label, idx) => {
                const active = config.days.includes(idx);
                return (
                  <TouchableOpacity
                    key={label}
                    style={[
                      styles.dayBtn,
                      {
                        backgroundColor: active ? c.accent : c.surfaceVariant,
                        borderColor: active ? c.accent : c.border,
                      },
                    ]}
                    onPress={() => toggleDay(idx)}
                    disabled={savingConfig}
                  >
                    <Text style={[styles.dayBtnText, { color: active ? c.accentForeground : c.textSecondary }]}>
                      {label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            {savingConfig && (
              <ActivityIndicator size="small" color={c.accent} style={{ marginTop: 8 }} />
            )}
          </View>
        </ScrollView>
      ) : (
        <ScrollView
          contentContainerStyle={styles.scroll}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />}
        >
          {responses.length === 0 ? (
            <View style={styles.emptyContainer}>
              <ClipboardCheck size={48} color={c.textTertiary} />
              <Text style={[styles.emptyTitle, { color: c.textSecondary }]}>No check-ins yet</Text>
              <Text style={[styles.emptyHint, { color: c.textTertiary }]}>
                Configure a schedule so the athlete gets reminded to check in.
              </Text>
            </View>
          ) : (
            responses.map((resp) => (
              <View
                key={resp.id}
                style={[styles.responseCard, { backgroundColor: c.surface, borderColor: c.border }]}
              >
                <Text style={[styles.responseDate, { color: c.accent }]}>
                  {new Date(resp.submitted_at).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </Text>
                {Object.entries(resp.answers).map(([qId, val]) => (
                  <View key={qId} style={[styles.answerRow, { borderColor: c.border }]}>
                    <Text style={[styles.answerLabel, { color: c.textSecondary }]}>
                      {getQuestionLabel(qId)}
                    </Text>
                    <Text style={[styles.answerValue, { color: c.text }]}>
                      {formatAnswer(val as string | number | boolean)}
                    </Text>
                  </View>
                ))}
              </View>
            ))
          )}
          <View style={{ height: 40 }} />
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  backBtn: { padding: 4 },
  headerTitle: { fontSize: 20, fontFamily: TYPO.heading.bold },
  headerSub: { fontSize: 12, fontFamily: TYPO.body.regular, marginTop: 1 },
  tabRow: {
    flexDirection: 'row',
    marginHorizontal: 16,
    borderBottomWidth: 1,
  },
  tab: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 10,
  },
  tabText: {
    fontSize: 14,
    fontFamily: TYPO.heading.semibold,
  },
  scroll: { padding: 16 },
  card: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 14,
    gap: 12,
  },
  configRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  configLabel: {
    fontSize: 15,
    fontFamily: TYPO.body.semibold,
  },
  configHint: {
    fontSize: 12,
    fontFamily: TYPO.body.regular,
  },
  sectionTitle: {
    fontSize: 12,
    fontFamily: TYPO.body.bold,
    letterSpacing: 1,
    marginBottom: 4,
  },
  daysRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  dayBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 2,
    borderWidth: 1,
    minWidth: 44,
    alignItems: 'center',
  },
  dayBtnText: {
    fontSize: 12,
    fontFamily: TYPO.body.bold,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 60,
    gap: 12,
  },
  emptyTitle: {
    fontSize: 16,
    fontFamily: TYPO.body.semibold,
  },
  emptyHint: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
    textAlign: 'center',
    paddingHorizontal: 24,
  },
  responseCard: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 14,
    marginBottom: 12,
    gap: 10,
  },
  responseDate: {
    fontSize: 13,
    fontFamily: TYPO.body.bold,
    marginBottom: 4,
  },
  answerRow: {
    paddingVertical: 6,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: 2,
  },
  answerLabel: {
    fontSize: 12,
    fontFamily: TYPO.body.semibold,
  },
  answerValue: {
    fontSize: 14,
    fontFamily: TYPO.body.regular,
  },
});
